"use client";
import React, { useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";
import { Box, SimpleGrid, Stack, Tabs as ChakraTabs } from "@chakra-ui/react";
import type { ResponsiveValue } from "@chakra-ui/styled-system";

type Props = {
  columns?: ResponsiveValue<number>;
  tabList: {
    component: React.ReactElement<any>;
    label: React.FunctionComponent<{ isSelected: boolean }>;
  }[];
};

const useHandleTab = () => {
  const searchParams = useSearchParams();
  const tab = searchParams.get("tab");

  const initialTabIndex = useMemo(() => (tab ? parseInt(tab) : 0), [tab]);

  const [tabIndex, setTabIndex] = useState(initialTabIndex);

  const activeTabIndex = tab ? parseInt(tab) : tabIndex;

  return { tabIndex: activeTabIndex, setTabIndex };
};

const Tabs = (props: Props) => {
  const { columns = { base: 1, sm: 3 }, tabList } = props;
  const { tabIndex, setTabIndex } = useHandleTab();

  return (
    <Stack gap={6} w="100%">
      <SimpleGrid columns={columns} gap={2} w="full">
        {tabList.map((tab, index) => {
          const isSelected = index === tabIndex;
          const { label: Label } = tab;
          return (
            <Box
              bg={isSelected ? "primary.200" : "transparent"}
              borderColor="primary.200"
              borderRadius={"8px"}
              borderWidth={"1px"}
              boxSizing={"border-box"}
              className="brand-shadow"
              color={isSelected ? "white" : "inherit"}
              cursor="pointer"
              key={index}
              minH={"3rem"}
              onClick={() => setTabIndex(index)}
              p={3}
              transition={"background-color 0.1s ease"}
              w="full"
            >
              <Label isSelected={isSelected} />
            </Box>
          );
        })}
      </SimpleGrid>
      <ChakraTabs.Root value={tabIndex.toString()}>
        <ChakraTabs.ContentGroup mt={8}>
          {tabList.map((tab, index) => {
            return (
              <ChakraTabs.Content
                key={index}
                value={index.toString()}
                p={0}
                hidden={tabIndex !== index}
                aria-hidden={tabIndex !== index}
              >
                {tab.component}
              </ChakraTabs.Content>
            );
          })}
        </ChakraTabs.ContentGroup>
      </ChakraTabs.Root>
    </Stack>
  );
};

export default Tabs;
